import React from 'react';
import styled from 'styled-components';
import { Link } from 'gatsby';

import BasicPlayButton from '@basic/buttons/BasicPlayButton';

import { MdChevronRight } from 'react-icons/md';

// Link Base

const MenuLink = styled(Link)`
	font-family: ${({ theme }) => theme.fonts.subheadingMedium};
	font-size: 1.5rem;
	line-height: 100%; /* 15px */
	letter-spacing: 0.15px;
	color: ${({ theme }) => theme.colors.black.main};
`;

const MenuSubheaderWrapper = styled.span`
	font-family: ${({ theme }) => theme.fonts.body};
	font-size: 1.5rem;
	line-height: 24px; /* 160% */
	letter-spacing: 0.15px;
	color: ${({ theme }) => theme.colors.black.main};
`;

const MenuSubheader = ({ children }) => {
	return (
		<MenuSubheaderWrapper className="link__subhead">
			{children}
		</MenuSubheaderWrapper>
	);
};

// LINKS

const IconLinkWrapper = styled(MenuLink)`
	display: flex;
	gap: 0.75rem;
	align-items: center;
	color: ${({ theme }) => theme.colors.black.main};

	:hover {
		cursor: pointer;
		color: ${({ theme }) => theme.colors.primary.main};

		.icon-wrapper {
			img {
				filter: brightness(0) invert(1);
			}

			:after {
				background-color: ${({ theme }) => theme.colors.primary.main};
			}
		}
	}
`;

const IconImg = styled.div`
	display: block;
	position: relative;
	z-index: 0;

	img {
		padding: 10px;
		z-index: 1;
		position: relative;
	}

	&:after {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background-color: ${({ theme }) => theme.colors.white.secondary};
		border-radius: 10px;
		transition: all 0.5s ease;
	}
`;

export const IconLink = ({ link, image, header }) => {
	return (
		<IconLinkWrapper to={link}>
			<IconImg className="icon-wrapper">
				<img src={image} />
			</IconImg>
			{header}
		</IconLinkWrapper>
	);
};

const ChevronLinkWrapper = styled(MenuLink)`
	display: flex;
	align-items: center;

	background: linear-gradient(
		${({ theme }) => theme.colors.primary.main},
		${({ theme }) => theme.colors.primary.main}
	);
	background-size: 0 2px;
	background-position: 0 100%;
	background-repeat: no-repeat;
	padding-bottom: 6px;
	transition:
		background-size 0.75s ease,
		color 0.75s ease;
	width: max-content;

	svg {
		width: 20px;
		height: 20px;
		fill: ${({ theme }) => theme.colors.black.main};
	}

	:hover {
		background-size: 100% 2px;
		color: ${({ theme }) => theme.colors.primary.main};

		svg {
			fill: ${({ theme }) => theme.colors.primary.main};
		}
	}
`;

export const ChevronLink = ({ link, header }) => {
	return (
		<ChevronLinkWrapper to={link}>
			{header}
			<MdChevronRight />
		</ChevronLinkWrapper>
	);
};

const FullFeatureLinkWrapper = styled(Link)`
	border-radius: 16px;
	background: ${({ theme }) => theme.colors.white.secondary};
	padding: 16px;
	display: block;
	width: fit-content;

	.video-card__play-img {
		width: 100%;
		position: relative;

		.play-button__wrapper {
			position: absolute;
			left: 50%; /* Centers horizontally */
			top: 50%; /* Centers vertically */
			transform: translate(
				-50%,
				-50%
			); /* Offsets by half of its width & height */
		}
	}

	:hover {
		.play-button__wrapper {
			background-color: ${({ theme }) => theme.colors.primary.dark};
		}

		.link__subhead {
			color: ${({ theme }) => theme.colors.primary.main};
		}
	}
`;

const FullImg = styled.img`
	margin-bottom: 16px;
`;

export const FullFeatureLink = ({ image, link, subheader }) => {
	return (
		<FullFeatureLinkWrapper to={link}>
			{/* TODO FUTURE: Add a 'video' option in contentful if we need a version of this full feature that isn't a video */}
			<div className="video-card__play-img">
				<BasicPlayButton width={55} height={55} />
				<FullImg src={image} />
			</div>
			{subheader && <MenuSubheader>{subheader}</MenuSubheader>}
		</FullFeatureLinkWrapper>
	);
};

const SubtextLinkWrapper = styled(MenuLink)`
	padding-bottom: 16px;
	display: flex;
	flex-direction: column;

	.link__header {
		color: ${({ theme }) => theme.colors.black.main};
	}

	:hover {
		cursor: pointer;

		.link__header {
			color: ${({ theme }) => theme.colors.primary.main};
		}

		.link__subhead {
			color: ${({ theme }) => theme.colors.black.main};
		}
	}
`;

export const SubtextLink = ({ link, header, subheader }) => {
	return (
		<SubtextLinkWrapper to={link}>
			<span className="link__header">{header}</span>
			{subheader && <MenuSubheader>{subheader}</MenuSubheader>}
		</SubtextLinkWrapper>
	);
};

const LargeFeatureLinkWrapper = styled(MenuLink)`
	margin-bottom: 16px;
	display: flex;
	align-items: center;

	.link__header {
		color: ${({ theme }) => theme.colors.black.main};
	}

	:hover {
		cursor: pointer;

		.link__header {
			color: ${({ theme }) => theme.colors.primary.main};
		}

		.link__subhead {
			color: ${({ theme }) => theme.colors.black.main};
		}
	}
`;

const LargeImg = styled.img`
	max-width: 150px;
	margin-right: 16px;
`;

export const LargeFeatureLink = ({ image, link, header, subheader }) => {
	return (
		<LargeFeatureLinkWrapper to={link}>
			<LargeImg src={image} />
			<div>
				<span className="link__header">{header}</span>
				{subheader && <MenuSubheader>{subheader}</MenuSubheader>}
			</div>
		</LargeFeatureLinkWrapper>
	);
};
